import React, { useEffect, useRef, useState } from 'react';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Animated, Platform, StyleSheet, Text } from 'react-native';
import colors from '../../lib/colors';
import { fonts, interSemiBold } from '../../lib/fonts';
import { responsive } from '../../lib/responsive';

const ROTATING_WORD_SWAP_MS = 3500;
const ROTATING_WORD_TRANSITION_MS = 220;
const ROTATING_WORDS = [
    msg({ id: 'home.hero.rotating.inspires', message: 'inspires', comment: 'Rotating verb in the Paperzilla home-page hero' }),
    msg({ id: 'home.hero.rotating.invalidates', message: 'invalidates', comment: 'Rotating verb in the Paperzilla home-page hero' }),
    msg({ id: 'home.hero.rotating.supports', message: 'supports', comment: 'Rotating verb in the Paperzilla home-page hero' }),
    msg({ id: 'home.hero.rotating.scoops', message: 'scoops', comment: 'Rotating verb in the Paperzilla home-page hero' }),
    msg({ id: 'home.hero.rotating.validates', message: 'validates', comment: 'Rotating verb in the Paperzilla home-page hero' }),
    msg({ id: 'home.hero.rotating.outdates', message: 'outdates', comment: 'Rotating verb in the Paperzilla home-page hero' }),
    msg({ id: 'home.hero.rotating.strengthens', message: 'strengthens', comment: 'Rotating verb in the Paperzilla home-page hero' }),
    msg({ id: 'home.hero.rotating.preempts', message: 'preempts', comment: 'Rotating verb in the Paperzilla home-page hero' }),
];

export default function HomeHeroRotatingWord({ fontsLoaded }: { fontsLoaded: boolean }) {
    const { i18n } = useLingui();
    const [wordIndex, setWordIndex] = useState(0);
    const [isWordVisibleWeb, setIsWordVisibleWeb] = useState(true);
    const wordSwapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const slideAnim = useRef(new Animated.Value(1)).current;

    useEffect(() => {
        const interval = setInterval(() => {
            if (Platform.OS === 'web') {
                setIsWordVisibleWeb(false);
                if (wordSwapTimeoutRef.current) {
                    clearTimeout(wordSwapTimeoutRef.current);
                }

                wordSwapTimeoutRef.current = setTimeout(() => {
                    setWordIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
                    setIsWordVisibleWeb(true);
                    wordSwapTimeoutRef.current = null;
                }, ROTATING_WORD_TRANSITION_MS);
                return;
            }

            setWordIndex((prev) => (prev + 1) % ROTATING_WORDS.length);
        }, ROTATING_WORD_SWAP_MS);

        return () => {
            clearInterval(interval);
            if (wordSwapTimeoutRef.current) {
                clearTimeout(wordSwapTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (Platform.OS === 'web') {
            return;
        }

        slideAnim.setValue(0);
        Animated.timing(slideAnim, {
            toValue: 1,
            duration: 400,
            useNativeDriver: true,
        }).start();
    }, [wordIndex, slideAnim]);

    const currentWord = i18n._(ROTATING_WORDS[wordIndex]);

    if (Platform.OS === 'web') {
        return (
            <>
                {/* One grid cell reserves the widest localized word, including before hydration. */}
                {ROTATING_WORDS.map((word) => (
                    <Text
                        key={word.id}
                        aria-hidden={true}
                        style={[fonts.displayLarge, styles.heroTitle, styles.wordSizer, fontsLoaded && interSemiBold]}
                        {...responsive('hero-title')}
                    >
                        {i18n._(word)}
                    </Text>
                ))}
                <Text
                    style={[
                        fonts.displayLarge,
                        styles.heroTitle,
                        styles.rotatingWordWeb,
                        isWordVisibleWeb ? styles.rotatingWordWebVisible : styles.rotatingWordWebHidden,
                        fontsLoaded && interSemiBold,
                    ]}
                    {...responsive('hero-title')}
                >
                    {currentWord}
                </Text>
            </>
        );
    }

    return (
        <Animated.Text
            style={[
                fonts.displayLarge,
                styles.heroTitle,
                styles.rotatingWord,
                fontsLoaded && interSemiBold,
                {
                    opacity: slideAnim,
                    transform: [{
                        translateY: slideAnim.interpolate({
                            inputRange: [0, 1],
                            outputRange: [16, 0],
                        }),
                    }],
                },
            ]}
            {...responsive('hero-title')}
        >
            {currentWord}
        </Animated.Text>
    );
}

const styles = StyleSheet.create({
    heroTitle: {
        color: colors.primaryGreenDark,
        fontWeight: '600',
        textAlign: 'center',
    },
    rotatingWord: {
        left: 0,
        position: 'absolute',
        right: 0,
        textAlign: 'center',
        top: 0,
    },
    rotatingWordWeb: {
        ...(Platform.OS === 'web' && ({
            gridArea: 'word',
            transitionDuration: `${ROTATING_WORD_TRANSITION_MS}ms`,
            transitionProperty: 'opacity, transform',
            transitionTimingFunction: 'ease-out',
            willChange: 'opacity, transform',
        } as any)),
    },
    wordSizer: {
        ...(Platform.OS === 'web' && ({
            gridArea: 'word',
            visibility: 'hidden',
            pointerEvents: 'none',
            userSelect: 'none',
        } as any)),
    },
    rotatingWordWebVisible: {
        opacity: 1,
        transform: [{ translateY: 0 }],
    },
    rotatingWordWebHidden: {
        opacity: 0,
        transform: [{ translateY: 10 }],
    },
});
